import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchComplaints } from "../services/api";
import api, { errorMessage, getAllPages, logError } from "../services/api";
import { useRiderUpdates } from "../services/adminSocket";
import {
  ArrowLeft,
  Mail,
  Phone,
  User,
  Star,
  MapPin,
  DollarSign,
  MessageSquare,
  Filter,
} from 'lucide-react';
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import RefreshButton from "../components/RefreshButton";
import toast from "react-hot-toast";
import {
  fetchAllPages,
  getCollection,
  normalizeCommission,
  normalizeRide,
  recordBelongsToRider,
  resolveRiderIdentity,
  sumNumbers,
  sumRiderCredits,
} from "../utils/riderManagement";
import { formatLagosDate } from "../utils/tripLog";

const DriverDetails = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [driver, setDriver] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState(null);
  const [complaintsRetryToken, setComplaintsRetryToken] = useState(0);
  const [rides, setRides] = useState([]);
  const [ridesLoading, setRidesLoading] = useState(false);
  const [ridesError, setRidesError] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsError, setEarningsError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [lifetimePayoutNumber, setLifetimePayoutNumber] = useState(null);
  const [lifetimePayoutSource, setLifetimePayoutSource] = useState('unavailable');
  const [payoutBreakdown, setPayoutBreakdown] = useState(null);
  // { completed, cancelled, total } from GET /admin/riders/:userId/rides
  const [tripSummary, setTripSummary] = useState(null);
  const [tripSummaryError, setTripSummaryError] = useState(false);
  const [grossFareNumber, setGrossFareNumber] = useState(null);
  const [commissionTotalNumber, setCommissionTotalNumber] = useState(null);
  const [ridesRetryToken, setRidesRetryToken] = useState(0);
  const [earningsRetryToken, setEarningsRetryToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  // Background reloads keep what's on screen: no spinner, and a failed fetch
  // never blanks the page or falls back to the stale cached list.
  const silentReload = useRef(false);
  const manualRefresh = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const toDisplayValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const formatDateValue = (value) => {
    return formatLagosDate(value, 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatStatus = (value) => {
    if (!value) return 'N/A';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const formatBooleanValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return value ? 'Yes' : 'No';
  };

  const formatCurrencyValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const amount = Number(value);
    if (Number.isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusPillClass = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'completed') return 'bg-green-100 text-green-800';
    if (normalized === 'active' || normalized === 'ongoing') return 'bg-orange-100 text-orange-800';
    if (normalized === 'requested') return 'bg-blue-100 text-blue-800';
    return 'bg-red-100 text-red-800';
  };


  const getEarningStatusPillClass = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'completed' || normalized === 'success') return 'bg-green-100 text-green-800';
    if (normalized === 'active' || normalized === 'pending' || normalized === 'processing') return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  // Initialize driver data - simplified version
  useEffect(() => {
    let isMounted = true;

    const silent = silentReload.current;
    silentReload.current = false;

    const loadData = async () => {
      try {
        if (!silent) setLoading(true);

        let foundDriver = null;

        try {
          const response = await api.get(`/v1/admin/riders/${id}`);
          foundDriver = response.data?.data || response.data;
        } catch (singleFetchError) {
          if (silent) {
            logError('Background rider reload failed; keeping current view:', singleFetchError);
            if (manualRefresh.current) {
              toast.error("Couldn't refresh this rider. Showing the last loaded details.");
            }
            return;
          }
          logError('Rider-by-id fetch failed, trying local cache/list fallback:', singleFetchError);

          const drivers = JSON.parse(localStorage.getItem("drivers") || "[]");
          foundDriver = drivers.find((d) => d.id === id || d._id === id || d.user?._id === id);

          if (!foundDriver) {
            try {
              const response = await api.get('/v1/admin/riders');
              if (response.data?.data && isMounted) {
                const allRiders = response.data.data;
                foundDriver = allRiders.find((r) =>
                  r.id === id || r._id === id || r.user?._id === id
                );

                if (foundDriver) {
                  localStorage.setItem("drivers", JSON.stringify(allRiders));
                }
              }
            } catch (apiError) {
              logError('Error fetching riders list from API:', apiError);
            }
          }
        }

        if (!isMounted) return;

        if (!foundDriver) {
          console.error('Rider not found with ID:', id);
          if (!silent) setLoading(false);
          return;
        }

        if (foundDriver) {
          const userProfile = foundDriver.user || {};
          const documents = foundDriver.documents || {};

          const transformedDriver = {
            id: foundDriver.id || foundDriver._id,
            _id: foundDriver._id,
            name: userProfile.name || foundDriver.name || 'N/A',
            email: userProfile.email || foundDriver.email || 'N/A',
            phone: userProfile.phone || foundDriver.phone || 'N/A',
            mobile: userProfile.phone || foundDriver.mobile || 'N/A',
            status: foundDriver.status || 'pending',
            nin: foundDriver.nin || 'N/A',
            riderId: foundDriver.riderId || userProfile.riderId || 'N/A',
            // Permanent Zooma code (Z042). Distinct from riderId (GRN-04),
            // which encodes the physical sticker and changes when one is
            // replaced — this one never does, so it is what support quotes.
            zoomaId: foundDriver.zoomaId || userProfile.zoomaId || null,
            stickerNumber: foundDriver.stickerNumber || null,
            stickerColour: foundDriver.stickerColour || null,
            kekeType: foundDriver.kekeType || null,
            kekeColour: foundDriver.kekeColour || null,
            plateNumber: toDisplayValue(foundDriver.plateNumber),
            nextOfKinName: foundDriver.nextOfKinName || null,
            nextOfKinPhone: foundDriver.nextOfKinPhone || null,
            guarantor1Name: foundDriver.guarantor1Name || null,
            guarantor1Phone: foundDriver.guarantor1Phone || null,
            guarantor2Name: foundDriver.guarantor2Name || null,
            guarantor2Phone: foundDriver.guarantor2Phone || null,
            dob: foundDriver.dob || userProfile.dateOfBirth || null,
            dateOfBirth: foundDriver.dob || userProfile.dateOfBirth || null,
            gender: foundDriver.gender || userProfile.gender || null,
            address: foundDriver.address || userProfile.address || null,
            vehicleMakeModel: foundDriver.vehicleMakeModel || foundDriver.makeModel || foundDriver.vehicle?.makeModel || null,
            vehicleYear: foundDriver.vehicleYear || foundDriver.year || foundDriver.vehicle?.year || null,
            vehicleColor: foundDriver.vehicleColor || foundDriver.color || foundDriver.vehicle?.color || null,
            licenseDocument: documents.driverLicenseUrl || foundDriver.license || null,
            passportPhotoUrl: documents.passportPhotoUrl || null,
            kekeFrontUrl: documents.kekeFrontUrl || null,
            kekeBackUrl: documents.kekeBackUrl || null,
            licenseExpiryDate: foundDriver.licenseExpiryDate || foundDriver.licenseExpiry || null,
            licenseStateIssued: foundDriver.licenseStateIssued || foundDriver.licenseState || null,
            profileImage: userProfile.profilePicture || userProfile.profileImage || foundDriver.profileImage || '',
            role: userProfile.role || null,
            isActive: userProfile.isActive,
            isVerified: userProfile.isVerified,
            documents,
            createdAt: foundDriver.createdAt || new Date().toISOString(),
            updatedAt: foundDriver.updatedAt || new Date().toISOString(),
            ...foundDriver,
            user: userProfile
          };

          setDriver(transformedDriver);
          setUpdatedAt(new Date());
        } else {
          setDriver(null);
        }
      } catch (err) {
        logError('Error in loadData:', err);
        if (isMounted && !silent) {
          setDriver(null);
        }
      } finally {
        manualRefresh.current = false;
        if (isMounted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, activeTab, reloadKey]); // Added activeTab to dependencies

  // Keyed on the id, not the object: loadData re-sets driver on every tab change.
  const driverId = driver?.id;

  useEffect(() => {
    if (!driverId || activeTab !== 'complaints') return undefined;

    let isMounted = true;

    const loadComplaints = async () => {
      setComplaintsLoading(true);
      setComplaintsError(null);

      try {
        const data = await fetchComplaints({ driverId });
        const list = Array.isArray(data?.data) ? data.data : data;
        if (!Array.isArray(list)) throw new Error('Unexpected complaints response');
        if (isMounted) setComplaints(list);
      } catch (err) {
        logError('Failed to fetch complaints:', err);
        if (isMounted) {
          setComplaints([]);
          setComplaintsError("Couldn't load complaints.");
        }
      } finally {
        if (isMounted) setComplaintsLoading(false);
      }
    };

    loadComplaints();
    return () => {
      isMounted = false;
    };
  }, [activeTab, driverId, complaintsRetryToken]);

  // Trips this rider accepted, by their user id. The summary counts every
  // completed trip; the old count was ride sessions, not trips.
  const riderUserId = driver ? resolveRiderIdentity(driver, id).userId : null;

  const reloadRider = ({ manual = false } = {}) => {
    silentReload.current = true;
    manualRefresh.current = manual;
    if (manual) setRefreshing(true);
    setReloadKey((k) => k + 1);
  };

  useRiderUpdates(reloadRider, riderUserId || null);

  useEffect(() => {
    if (!riderUserId) return undefined;
    let isMounted = true;
    setTripSummaryError(false);
    api.get(`/v1/admin/riders/${riderUserId}/rides`, { params: { page: 1, limit: 1 } })
      .then((response) => {
        if (isMounted) setTripSummary(response.data?.summary || null);
      })
      .catch((summaryError) => {
        logError('Failed to load rider trip summary:', summaryError);
        if (isMounted) setTripSummaryError(true);
      });
    return () => {
      isMounted = false;
    };
  }, [riderUserId, ridesRetryToken]);

  useEffect(() => {
    if (!riderUserId || activeTab !== 'rides') return undefined;

    let isMounted = true;

    const loadRides = async () => {
      setRidesLoading(true);
      setRidesError(null);

      try {
        const result = await fetchAllPages(
          (page, limit) => api.get(`/v1/admin/riders/${riderUserId}/rides`, { params: { page, limit } })
            .then((response) => response.data),
          100,
        );
        if (isMounted) setRides(result.records.map(normalizeRide));
      } catch (ridesError) {
        logError('Failed to load rider rides:', ridesError);
        if (isMounted) {
          setRides([]);
          setRidesError(ridesError.response?.data?.message || 'Unable to load rides.');
        }
      } finally {
        if (isMounted) setRidesLoading(false);
      }
    };

    loadRides();
    return () => {
      isMounted = false;
    };
  }, [activeTab, riderUserId, ridesRetryToken]);

  useEffect(() => {
    if (!driver || activeTab !== 'earnings') return undefined;

    let isMounted = true;
    const identity = resolveRiderIdentity(driver, id);

    const loadEarnings = async () => {
      setEarningsLoading(true);
      setEarningsError(null);
      setLifetimePayoutNumber(null);
      setLifetimePayoutSource('unavailable');
      setPayoutBreakdown(null);

      // Wallet and commissions load independently: a failed commissions list
      // must not hide the payout total, which comes from the wallet.
      const walletPromise = identity.userId
        ? Promise.allSettled([
            api.get(`/v1/admin/wallets/${identity.userId}`),
            api.get(`/v1/admin/wallets/${identity.userId}/transactions`),
          ])
        : Promise.resolve(null);
      // The commissions endpoint 400s on ?riderId=, so page through all of them
      // and keep this rider's rows here.
      const commissionsPromise = getAllPages('/v1/admin/commissions')
        .then((records) => records.filter((record) => recordBelongsToRider(record, identity)));

      const [walletResults, commissionResult] = await Promise.all([
        walletPromise,
        commissionsPromise.then((rows) => ({ rows }), (error) => ({ error })),
      ]);
      if (!isMounted) return;

      let walletCredits = null;
      if (walletResults) {
        const [balanceRes, transactionsRes] = walletResults;
        if (balanceRes.status === 'fulfilled') {
          const walletPayload = balanceRes.value.data?.data || balanceRes.value.data || {};
          const balanceKobo = Number(walletPayload.balanceKobo);
          setWalletBalance(Number.isFinite(balanceKobo) ? balanceKobo / 100 : null);
        } else {
          logError('Unable to fetch rider wallet balance:', balanceRes.reason);
        }
        if (transactionsRes.status === 'fulfilled') {
          walletCredits = sumRiderCredits(getCollection(transactionsRes.value.data, ['transactions']));
        } else {
          logError('Unable to fetch rider wallet transactions:', transactionsRes.reason);
        }
      }

      let commissionPayout = null;
      if (commissionResult.error) {
        logError('Failed to load rider earnings:', commissionResult.error);
        setEarnings([]);
        setEarningsError(errorMessage(commissionResult.error, 'Unable to load earnings.'));
      } else {
        const mappedEarnings = commissionResult.rows.map(normalizeCommission);
        const hasGrossValues = mappedEarnings.some((item) => Number.isFinite(item.amountNumber));
        const hasCommissionValues = mappedEarnings.some((item) => Number.isFinite(item.commissionNumber));
        commissionPayout = mappedEarnings.some((item) => Number.isFinite(item.payoutNumber))
          ? sumNumbers(mappedEarnings, 'payoutNumber')
          : null;
        setEarnings(mappedEarnings);
        setGrossFareNumber(hasGrossValues ? sumNumbers(mappedEarnings, 'amountNumber') : null);
        setCommissionTotalNumber(hasCommissionValues ? sumNumbers(mappedEarnings, 'commissionNumber') : null);
      }

      if (walletCredits !== null) {
        // Ride earnings plus promotion top-ups: everything credited to the rider.
        setLifetimePayoutNumber(walletCredits.total);
        setPayoutBreakdown(walletCredits);
        setLifetimePayoutSource('wallet_transactions');
      } else if (commissionPayout !== null) {
        setLifetimePayoutNumber(commissionPayout);
        setLifetimePayoutSource('commission_fallback');
      }
      setEarningsLoading(false);
    };

    loadEarnings();
    return () => {
      isMounted = false;
    };
  }, [activeTab, driver, earningsRetryToken, id]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col w-full lg:pl-64">
          <Topbar />
          <main className="flex-1 overflow-y-auto p-6 flex items-center justify-center">
            <div className="text-gray-500">Loading...</div>
          </main>
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col w-full lg:pl-64">
          <Topbar />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Rider Not Found</h2>
              <button
                onClick={() => navigate("/drivers")}
                className="text-[#FF8C00] hover:underline"
              >
                Back to Drivers
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const tabItems = [
    { id: "profile", label: "Profile", icon: User },
    { id: "rides", label: "Rides", icon: MapPin },
    { id: "earnings", label: "Earnings", icon: DollarSign },
    {
      id: "complaints",
      label: "Complaints/Feedbacks",
      icon: MessageSquare,
      onSelect: () => handleTabChange("complaints")
    },
  ];


  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Keep the earnings summary tied to normalized payout values rather than raw rows.
  const totalLifetimeEarning = lifetimePayoutNumber;
  const totalCommission = commissionTotalNumber;
  const grossFares = grossFareNumber;
  const driverRating = driver.averageRating ?? driver.user?.averageRating ?? null;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-[#FF8C00] transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              <span>Back to Riders</span>
            </button>
            <RefreshButton
              onRefresh={() => reloadRider({ manual: true })}
              loading={refreshing}
              updatedAt={updatedAt}
            />
          </div>

          {/* Header with Profile */}
          <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
              {/* Left: Profile Image and Basic Info */}
              <div className="flex gap-4 flex-1 min-w-0">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full border-2 border-gray-200 overflow-hidden">
                    {driver.profileImage && !driver.profileImage.startsWith('blob:') ? (
                      <img
                        src={driver.profileImage}
                        alt={driver.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-bold ${driver.profileImage && !driver.profileImage.startsWith('blob:') ? 'hidden' : ''}`}>
                      {driver.name?.charAt(0)?.toUpperCase() || 'D'}
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                <div className="flex-1 flex gap-8">
                  <div>
                    <h1 className="text-lg font-bold text-gray-900 mb-1">
                      {driver.name}
                    </h1>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Mail size={14} />
                      <span>{driver.email || "N/A"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={14} />
                      <span>{driver.mobile || "N/A"}</span>
                    </div>
                  </div>

                  {/* Earnings Info - shown only on earnings tab */}
                  {activeTab === "earnings" && (
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <span className="font-medium">Total lifetime payout:</span>
                        <span
                          className="font-semibold text-gray-900"
                          title={`Calculated from ${lifetimePayoutSource.replace('_', ' ')}`}
                        >
                          {formatCurrencyValue(totalLifetimeEarning)}
                        </span>
                      </div>
                      {payoutBreakdown ? (
                        <div className="text-xs text-gray-500 mb-1">
                          {formatCurrencyValue(payoutBreakdown.rides)} from rides +{' '}
                          {formatCurrencyValue(payoutBreakdown.promotions)} promo top-ups
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <span className="font-medium">Gross fares:</span>
                        <span className="font-semibold text-gray-900">{formatCurrencyValue(grossFares)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <span className="font-medium">Commission:</span>
                        <span className="font-semibold text-gray-900">{formatCurrencyValue(totalCommission)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-medium">Wallet balance:</span>
                        <span className="font-semibold text-gray-900">{formatCurrencyValue(walletBalance)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Status, Rides, and Rating Cards (hidden on earnings tab) */}
              <div className="flex flex-wrap gap-3 md:gap-4">
                {activeTab !== "earnings" && (
                  <>
                    {/* Status */}
                    <div className="bg-[#FFF4E6] rounded-lg px-4 py-3 min-w-[120px]">
                      <p className="text-xs text-gray-600 mb-1">Status</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#FF8C00] rounded-full"></div>
                        <span className="text-sm font-semibold text-gray-900">{formatStatus(driver.status)}</span>
                      </div>
                    </div>

                    {/* Completed trips from the server's count, not ride sessions. */}
                    <div className="bg-gray-50 rounded-lg px-4 py-3 min-w-[120px]">
                      <p className="text-xs text-gray-600 mb-1">Completed trips</p>
                      <span
                        className="text-sm font-semibold text-gray-900"
                        title={tripSummaryError ? "Couldn't load the trip count" : undefined}
                      >
                        {typeof tripSummary?.completed === 'number'
                          ? tripSummary.completed.toLocaleString()
                          : '—'}
                      </span>
                    </div>

                    {/* Rating — hidden until the API sends a real one */}
                    {driverRating > 0 && (
                      <div className="bg-gray-50 rounded-lg px-4 py-3 min-w-[120px]">
                        <p className="text-xs text-gray-600 mb-1">Rating</p>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={14}
                              className={
                                star <= Math.round(driverRating)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "fill-gray-300 text-gray-300"
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Action Button - Changes text based on active tab */}
                <button
                  onClick={() => {
                    if (activeTab === 'earnings' || activeTab === 'complaints') {
                      // Add filter functionality here
                      console.log('Filter clicked');
                    } else {
                      // Use User ID for navigation (backend expects user._id, not rider profile id)
                      const userId = driver.user?._id || driver.id;
                      navigate(`/drivers/edit/${userId}`);
                    }
                  }}
                  className="px-5 py-2 bg-[#FF8C00] text-black rounded-lg text-sm font-medium hover:bg-[#E67E22] h-fit flex items-center gap-2"
                >
                  {(activeTab === 'earnings' || activeTab === 'complaints') ? (
                    <>
                      <Filter size={16} />
                      Filter
                    </>
                  ) : 'Edit Profile'}
                </button>
              </div>
            </div>
          </div>

          {/* Content with Left Tab Navigation */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            {/* Left Tab Navigation */}
            <div className="w-full md:w-64 md:flex-shrink-0">
              <div className="bg-white dark:bg-black rounded-xl shadow-sm p-2">
                <nav className="space-y-1">
                  {tabItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === item.id
                          ? "bg-[#FF8C00] text-black"
                          : "text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        <Icon size={18} />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 min-w-0">
              {activeTab === "profile" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        Personal Information
                      </h3>
                      <div className="space-y-3">
                        <DetailRow label="Name" value={driver.name} />
                        <DetailRow label="Email" value={toDisplayValue(driver.email)} />
                        <DetailRow label="Phone Number" value={toDisplayValue(driver.mobile)} />
                        <DetailRow label="Date of Birth" value={formatDateValue(driver.dob || driver.user?.dateOfBirth)} />
                        <DetailRow label="Zooma ID" value={toDisplayValue(driver.zoomaId)} />
                        <DetailRow label="Rider ID" value={toDisplayValue(driver.riderId)} />
                        <DetailRow label="NIN" value={toDisplayValue(driver.nin)} />
                      </div>
                    </div>

                    {/* Vehicle Information */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        Vehicle Information
                      </h3>
                      <div className="space-y-3">
                        <DetailRow label="Sticker Number" value={toDisplayValue(driver.stickerNumber)} />
                        <DetailRow label="Sticker Color" value={toDisplayValue(driver.stickerColour)} />
                        <DetailRow label="Keke Type" value={toDisplayValue(driver.kekeType)} />
                        <DetailRow label="Keke Color" value={toDisplayValue(driver.kekeColour)} />
                        <DetailRow label="Plate Number" value={toDisplayValue(driver.plateNumber)} />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Account Information */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        Account Information
                      </h3>
                      <div className="space-y-3">
                        <DetailRow label="Username" value={toDisplayValue(driver.user?.name || driver.name)} />
                        <DetailRow label="Whatsapp Number" value={toDisplayValue(driver.user?.phone || driver.mobile)} />
                        <DetailRow label="Role" value={toDisplayValue(driver.role || driver.user?.role)} />
                        <DetailRow label="Active" value={formatBooleanValue(driver.isActive ?? driver.user?.isActive)} />
                        <DetailRow label="Verified" value={formatBooleanValue(driver.isVerified ?? driver.user?.isVerified)} />
                      </div>
                    </div>

                    {/* License Information */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        License Details
                      </h3>
                      <div className="space-y-3">
                        <DetailRow
                          label="License Document"
                          value={toDisplayValue(driver.licenseDocument || driver.documents?.driverLicenseUrl)}
                          download={!!(driver.licenseDocument || driver.documents?.driverLicenseUrl)}
                        />
                        <DetailRow
                          label="Passport Photo"
                          value={toDisplayValue(driver.passportPhotoUrl || driver.documents?.passportPhotoUrl)}
                          download={!!(driver.passportPhotoUrl || driver.documents?.passportPhotoUrl)}
                        />
                        <DetailRow
                          label="Keke Front"
                          value={toDisplayValue(driver.kekeFrontUrl || driver.documents?.kekeFrontUrl)}
                          download={!!(driver.kekeFrontUrl || driver.documents?.kekeFrontUrl)}
                        />
                        <DetailRow
                          label="Keke Back"
                          value={toDisplayValue(driver.kekeBackUrl || driver.documents?.kekeBackUrl)}
                          download={!!(driver.kekeBackUrl || driver.documents?.kekeBackUrl)}
                        />
                      </div>
                    </div>

                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        Next of Kin
                      </h3>
                      <div className="space-y-3">
                        <DetailRow label="Name" value={toDisplayValue(driver.nextOfKinName)} />
                        <DetailRow label="Phone" value={toDisplayValue(driver.nextOfKinPhone)} />
                      </div>
                    </div>

                    {/* Guarantor Information */}
                    <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">
                        Guarantor
                      </h3>
                      <div className="space-y-3">
                        <DetailRow label="Guarantor Name" value={toDisplayValue(driver.guarantor1Name)} />
                        <DetailRow label="Guarantor Phone" value={toDisplayValue(driver.guarantor1Phone)} />
                        <DetailRow label="Guarantor 2 Name" value={toDisplayValue(driver.guarantor2Name)} />
                        <DetailRow label="Guarantor 2 Phone" value={toDisplayValue(driver.guarantor2Phone)} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "rides" && (
                <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Rides History</h3>

                  {/* Rides Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Pickup</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Drop-off</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Ride time</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Amount</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-black divide-y divide-gray-200">
                        {ridesError ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-6 text-center text-sm text-red-600" aria-live="polite">
                              <div>{ridesError}</div>
                              <button
                                type="button"
                                onClick={() => setRidesRetryToken((current) => current + 1)}
                                className="mt-2 font-medium text-[#FF8C00] hover:underline"
                              >
                                Try again
                              </button>
                            </td>
                          </tr>
                        ) : ridesLoading ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-6 text-center text-sm text-gray-500" aria-live="polite">
                              Loading rides...
                            </td>
                          </tr>
                        ) : rides.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-6 text-center text-sm text-gray-500" aria-live="polite">
                              No rides found for this rider.
                            </td>
                          </tr>
                        ) : rides.map((ride) => (
                          <tr key={ride.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {ride.date}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {ride.pickup}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {ride.dropoff}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {ride.rideTime}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {ride.amount}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusPillClass(ride.statusType)}`}
                              >
                                {ride.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "earnings" && (
                <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Earnings</h3>

                  {/* Earnings Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Ride ID</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Booking Type</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Amount</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Commission</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Method</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-black divide-y divide-gray-200">
                        {earningsError ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-6 text-center text-sm text-red-600" aria-live="polite">
                              <div>{earningsError}</div>
                              <button
                                type="button"
                                onClick={() => setEarningsRetryToken((current) => current + 1)}
                                className="mt-2 font-medium text-[#FF8C00] hover:underline"
                              >
                                Try again
                              </button>
                            </td>
                          </tr>
                        ) : earningsLoading ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500" aria-live="polite">
                              Loading earnings...
                            </td>
                          </tr>
                        ) : earnings.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500" aria-live="polite">
                              No earnings found for this rider.
                            </td>
                          </tr>
                        ) : earnings.map((earning) => (
                          <tr key={earning.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.date}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.rideId}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.bookingType}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.amount}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.commission}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {earning.method}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span
                                className={`px-3 py-1 text-xs font-medium rounded-full ${getEarningStatusPillClass(earning.status)}`}
                              >
                                {earning.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "complaints" && (
                <div className="bg-white dark:bg-black rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Complaints/Feedbacks</h3>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Complaint ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reporting
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-black divide-y divide-gray-200">
                        {complaintsError ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 text-center text-sm text-red-600" aria-live="polite">
                              <div>{complaintsError}</div>
                              <button
                                type="button"
                                onClick={() => setComplaintsRetryToken((current) => current + 1)}
                                className="mt-2 font-medium text-[#FF8C00] hover:underline"
                              >
                                Try again
                              </button>
                            </td>
                          </tr>
                        ) : complaintsLoading ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500" aria-live="polite">
                              Loading complaints...
                            </td>
                          </tr>
                        ) : complaints.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500" aria-live="polite">
                              No complaints
                            </td>
                          </tr>
                        ) : complaints.map((item, index) => {
                          // Generate a more unique key using multiple identifiers and timestamp
                          const uniqueKey = `complaint-${item.id || 'no-id'}-${item.complaintId || 'no-cid'}-${Date.now()}-${index}`;
                          return (
                            <tr key={uniqueKey} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.date}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.complaintId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.category}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.reporting}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Resolved'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Helper component for displaying info rows
const InfoRow = ({ label, value }) => {
  if (!value) return null;

  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
};

// Helper component for detail rows in the profile tab
const DetailRow = ({ label, value, download }) => {
  const handleDownload = (e) => {
    if (!value || value === 'N/A') {
      e.preventDefault();
      return;
    }

    if (typeof value !== 'string') {
      e.preventDefault();
      return;
    }

    // If it's not a full URL or blob, assume it's a local file path
    if (!value.startsWith('http') && !value.startsWith('blob:')) {
      e.preventDefault();

      // Try to construct a proper URL
      const baseUrl = window.location.origin;
      const filePath = value.startsWith('/') ? value : `/${value}`;
      const fullUrl = `${baseUrl}${filePath}`;

      console.log('Attempting to access file at:', fullUrl);

      // Open in new tab to see if we can access it
      window.open(fullUrl, '_blank');
      return;
    }
  };

  return (
    <div className="flex justify-between items-center py-2">
      <span className="text-sm text-gray-600">{label}</span>
      {download && typeof value === 'string' && value !== 'N/A' ? (
        <a
          href={value}
          download
          className="text-sm text-black hover:text-orange-800 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleDownload}
        >
          {value ? value.split('/').pop() : 'Download'}
        </a>
      ) : (
        <span className="text-sm text-gray-900">{value}</span>
      )}
    </div>
  );
};

export default DriverDetails;
